import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowUpRight, Database } from 'lucide-react';
import { searchMps, PARTY_NAMES } from '../api';

export default function Hero({ meta }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(() => {
      searchMps(q.trim()).then(d => setResults(d.results || [])).catch(() => setResults([]));
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  return (
    <section className="relative bg-ink-800 text-cream-100 overflow-hidden">
      {/* layered backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(800px 360px at 78% 18%, rgba(200,137,74,0.18), transparent 60%), radial-gradient(900px 500px at 5% 90%, rgba(216,226,234,0.08), transparent 65%)'
        }} />
        {/* subtle grid pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.045]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="42" height="42" patternUnits="userSpaceOnUse">
              <path d="M 42 0 L 0 0 0 42" fill="none" stroke="#F7F3EC" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-24" ref={wrapRef}>
        {/* status bar */}
        <div className="flex flex-wrap items-center gap-4 mb-12">
          <span className="inline-flex items-center gap-2 kicker text-copper-500">
            <span className="h-1.5 w-1.5 rounded-full bg-copper-500 animate-pulse" /> Spill the Hill · Canada
          </span>
          <span className="hidden sm:inline-block h-3 w-px bg-cream-100/20" />
          <span className="text-[11px] uppercase tracking-[0.18em] text-cream-100/55 inline-flex items-center gap-2">
            <Database size={11} /> Live record · {meta?.latest_quarter || 'updating'}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <h1 className="font-serif text-[52px] md:text-[78px] leading-[0.98] tracking-[-0.02em] text-cream-100">
              What Canada<br />
              <span className="italic font-light text-copper-500">actually spends,</span><br />
              drawn from the<br className="hidden md:block" /> public record.
            </h1>

            <p className="mt-8 text-[17px] leading-[1.7] text-cream-100/75 max-w-xl">
              An accountability ledger built from the House of Commons, Elections Canada, Statistics Canada and open.canada.ca.
              Every figure traces back to a source you can re-verify.
            </p>

            <div className="mt-8 max-w-xl relative">
              <div className="relative">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none" />
                <input
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                  onFocus={() => setOpen(true)}
                  placeholder="Find your MP — start typing a name"
                  className="w-full pl-11 pr-32 py-4 rounded-sm bg-cream-50 border border-cream-50 text-[15px] text-ink-700 placeholder-ink-500 focus:outline-none focus:border-copper-500 focus:ring-2 focus:ring-copper-500/20 transition"
                />
                <button className="absolute right-1 top-1 bottom-1 px-4 bg-ink-800 hover:bg-ink-900 text-cream-100 text-[13px] font-medium tracking-wide rounded-sm inline-flex items-center gap-1 transition">
                  Search <ArrowUpRight size={13} />
                </button>
              </div>
              {open && results.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-cream-50 border border-ink-300 rounded-sm shadow-2xl max-h-80 overflow-auto z-20">
                  {results.map((m) => (
                    <button
                      key={m.slug}
                      onClick={() => { navigate(`/mp/${m.slug}`); setOpen(false); setQ(''); }}
                      className="w-full text-left px-4 py-2.5 hover:bg-cream-100 flex items-center justify-between text-[14px] transition"
                    >
                      <span className="text-ink-700 font-medium">{m.name}</span>
                      <span className="text-[12px] text-ink-500">{PARTY_NAMES[m.party]} · {m.province}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Live stats sidebar */}
          <div className="lg:col-span-4 lg:pl-6 lg:border-l lg:border-cream-100/12">
            <div className="kicker text-copper-500 mb-5">Live Counters</div>
            <Stat label="Sitting parliamentarians" value={meta?.total_mps || 343} />
            <Stat label="Quarters of data loaded" value="8" />
            <Stat label="Datasets tracked" value="7" />
            <Stat label="Canadian outlets monitored" value="12" />
          </div>
        </div>
      </div>

      {/* bottom edge decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-copper-500/50 to-transparent" />
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-baseline justify-between py-3.5 border-b border-cream-100/10 last:border-0">
      <span className="text-[13px] text-cream-100/65">{label}</span>
      <span className="font-serif text-[26px] text-cream-100 tabular">{value}</span>
    </div>
  );
}
